import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { Suspense, useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import { VStack, Text } from "@whoosh/design-system/src/components/primitive";

import { VerificationModalStepStateAtom } from "../../../../atoms/stores";
import { WelcomeStepFacilityEntry } from "./WelcomeStepFacilityEntry";
import { WelcomeStepQuery } from "./__generated__/WelcomeStepQuery.graphql";

export const WelcomeStep = () => {
  const verificationStepData = useAtomValue(VerificationModalStepStateAtom);
  const clubSlug =
    verificationStepData?.step === "Welcome"
      ? verificationStepData.clubSlug
      : undefined;

  const [queryRef, loadQuery] = useQueryLoader<WelcomeStepQuery>(
    WelcomeStepQueryString
  );

  useEffect(() => {
    if (clubSlug) {
      loadQuery({ clubSlug });
    }
  }, [clubSlug, loadQuery]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? (
        <WelcomeStepInner queryRef={queryRef} clubSlug={clubSlug} />
      ) : null}
    </Suspense>
  );
};

const WelcomeStepInner = ({
  queryRef,
  clubSlug,
}: {
  queryRef: PreloadedQuery<WelcomeStepQuery>;
  clubSlug?: string;
}) => {
  const data = usePreloadedQuery(WelcomeStepQueryString, queryRef);
  const clubName = data.unauthenticatedClub?.name ?? "";

  const facilities =
    data.unauthenticatedClub?.departments?.flatMap((department) =>
      department?.facilities?.map((facility) => ({
        id: facility?.id ?? "",
        name: facility?.name ?? "",
        slug: facility?.slug ?? "",
        departmentType: department?.type || "GOLF",
      }))
    ) || [];

  return (
    <VStack paddingHorizontal={6} paddingBottom={6} flexGrow={1} width="100%">
      <VStack flexGrow={1} mb={6}>
        <VStack gap={4}>
          <Text variant={"mainTitle2"}>{t`Welcome to ${clubName}!`}</Text>

          {facilities.length > 0 ? (
            <>
              <Text
                variant={"mainBaseMedium"}
                color="text-secondary"
              >{t`Where would you like to make your next booking?`}</Text>
              <VStack gap={2}>
                {facilities.map((facility, i) =>
                  facility ? (
                    <WelcomeStepFacilityEntry
                      key={facility?.id || i}
                      name={facility?.name}
                      facilitySlug={facility?.slug}
                      departmentType={facility?.departmentType}
                      isLast={i === facilities.length - 1}
                      clubSlug={clubSlug ?? ""}
                    />
                  ) : null
                )}
              </VStack>
            </>
          ) : (
            <Text
              variant={"mainBaseMedium"}
              color="text-secondary"
            >{t`No facilities found for this club.`}</Text>
          )}
        </VStack>
      </VStack>
    </VStack>
  );
};

export const WelcomeStepQueryString = graphql`
  query WelcomeStepQuery($clubSlug: String!) {
    unauthenticatedClub(slug: $clubSlug) {
      name
      departments {
        id
        type
        facilities {
          id
          name
          slug
        }
      }
    }
  }
`;
